import { useState, useEffect } from 'react';
import { BarChart, Bar, Cell, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';

const MESES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const COLORES_TEC  = ['#4cc9f0','#f72585','#7209b7','#4caf50','#ffc107'];
const COLORES_TIPO = ['#4cc9f0','#f72585','#7209b7','#3a0ca3','#4361ee'];
const COLORES_MARCA = ['#4cc9f0','#f72585','#7209b7','#4caf50','#ffc107','#ff9800','#e63946','#a855f7'];

// ══════════════════════════════════════════════════════════════
// Tab Vehículos
// ══════════════════════════════════════════════════════════════
function TabVehiculos({ mes, anio, esMesActual }) {
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(true);
  const [vista,    setVista]    = useState('marcas'); // marcas | modelos | anios

  useEffect(() => { cargar(); }, [mes, anio]);

  const cargar = async () => {
    setCargando(true);
    try {
      const data = await api.get(`/dashboard/vehiculos?mes=${mes}&anio=${anio}`);
      setDatos(data.error ? null : data);
    } catch(e) { console.error(e); }
    finally { setCargando(false); }
  };

  if (cargando) return <p style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Cargando datos de vehículos...</p>;
  if (!datos)   return <p style={{ color: '#e63946', padding: 20 }}>No se pudo cargar</p>;

  const maxMarca  = datos.top_marcas[0]?.ordenes  || 1;
  const maxModelo = datos.top_modelos[0]?.ordenes || 1;
  const maxAnio   = datos.por_anio[0]?.ordenes    || 1;

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { val: datos.total_ordenes, label: 'Órdenes identificadas', color: '#4cc9f0' },
          { val: datos.placas_unicas, label: 'Vehículos únicos',       color: '#a855f7' },
          { val: datos.top_marcas[0]?.marca || '—', label: 'Marca más atendida', color: '#ffc107', small: true },
          { val: datos.top_modelos[0]?.modelo?.split(' ').slice(0,3).join(' ') || '—', label: 'Modelo más frecuente', color: '#4caf50', small: true },
        ].map((k, i) => (
          <div key={i} style={{ background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 140, borderTop: `3px solid ${k.color}` }}>
            <div style={{ color: k.color, fontSize: k.small ? 16 : 28, fontWeight: 'bold', marginBottom: 6 }}>{k.val}</div>
            <div style={{ color: '#aaa', fontSize: 12 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'marcas',  label: '🏷️ Por Marca'  },
          { key: 'modelos', label: '🚗 Por Modelo'  },
          { key: 'anios',   label: '📅 Por Año'     },
        ].map(t => (
          <button key={t.key}
            style={{
              padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 'bold',
              background: vista === t.key ? '#1a2a3e' : 'none',
              border: vista === t.key ? '1px solid #4cc9f0' : '1px solid #2d2d44',
              color: vista === t.key ? '#4cc9f0' : '#888',
            }}
            onClick={() => setVista(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Vista: Por Marca ── */}
      {vista === 'marcas' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Gráfico */}
          <div style={sv.card}>
            <h3 style={sv.cardTitulo}>Top marcas atendidas</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={datos.top_marcas} layout="vertical" margin={{ left: 0, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" horizontal={false} />
                <XAxis type="number" stroke="#aaa" fontSize={11} />
                <YAxis type="category" dataKey="marca" stroke="#aaa" fontSize={11} width={70} />
                <Tooltip contentStyle={{ background: '#1e1e2e', border: 'none', color: '#fff', fontSize: 12 }}
                  formatter={(val) => [`${val} órdenes`, 'Total']} />
                <Bar dataKey="ordenes" radius={[0, 4, 4, 0]}>
                  {datos.top_marcas.map((_, i) => (
                    <Cell key={i} fill={COLORES_MARCA[i % COLORES_MARCA.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla */}
          <div style={sv.card}>
            <h3 style={sv.cardTitulo}>Detalle por marca</h3>
            <div style={sv.tablaHeader}>
              <span style={{ flex: 2 }}>MARCA</span>
              <span style={{ width: 70, textAlign: 'center' }}>VEHÍC.</span>
              <span style={{ width: 70, textAlign: 'center' }}>ÓRDS.</span>
              <span style={{ width: 60, textAlign: 'right' }}>%</span>
            </div>
            {datos.top_marcas.map((m, i) => (
              <div key={i} style={{ ...sv.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e' }}>
                <span style={{ flex: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: COLORES_MARCA[i % COLORES_MARCA.length], flexShrink: 0 }} />
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{m.marca}</span>
                  </div>
                  <div style={sv.barrita}>
                    <div style={{ ...sv.barritaFill, width: `${(m.ordenes / maxMarca) * 100}%`, background: COLORES_MARCA[i % COLORES_MARCA.length] }} />
                  </div>
                </span>
                <span style={{ width: 70, textAlign: 'center', color: '#a855f7', fontSize: 13 }}>{m.vehiculos}</span>
                <span style={{ width: 70, textAlign: 'center', color: '#fff', fontWeight: 'bold', fontSize: 13 }}>{m.ordenes}</span>
                <span style={{ width: 60, textAlign: 'right', color: '#555', fontSize: 12 }}>{m.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Vista: Por Modelo ── */}
      {vista === 'modelos' && (
        <div style={sv.card}>
          <h3 style={sv.cardTitulo}>Top 10 modelos más atendidos</h3>
          <div style={sv.tablaHeader}>
            <span style={{ flex: 1 }}>MARCA</span>
            <span style={{ flex: 3 }}>MODELO</span>
            <span style={{ width: 80, textAlign: 'center' }}>VEHÍC.</span>
            <span style={{ width: 80, textAlign: 'center' }}>ÓRDS.</span>
          </div>
          {datos.top_modelos.map((m, i) => (
            <div key={i} style={{ ...sv.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e', borderLeft: `3px solid ${COLORES_MARCA[i % COLORES_MARCA.length]}` }}>
              <span style={{ flex: 1, color: '#aaa', fontSize: 12 }}>{m.marca}</span>
              <span style={{ flex: 3 }}>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{m.modelo}</div>
                <div style={sv.barrita}>
                  <div style={{ ...sv.barritaFill, width: `${(m.ordenes / maxModelo) * 100}%`, background: COLORES_MARCA[i % COLORES_MARCA.length] }} />
                </div>
              </span>
              <span style={{ width: 80, textAlign: 'center', color: '#a855f7', fontSize: 13 }}>{m.vehiculos}</span>
              <span style={{ width: 80, textAlign: 'center', color: '#fff', fontWeight: 'bold', fontSize: 14 }}>{m.ordenes}</span>
            </div>
          ))}
          {datos.top_modelos.length === 0 && (
            <p style={{ color: '#555', textAlign: 'center', padding: 32 }}>Sin datos de modelos aún</p>
          )}
        </div>
      )}

      {/* ── Vista: Por Año ── */}
      {vista === 'anios' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={sv.card}>
            <h3 style={sv.cardTitulo}>Distribución por año del vehículo</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={datos.por_anio} layout="vertical" margin={{ left: 0, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" horizontal={false} />
                <XAxis type="number" stroke="#aaa" fontSize={11} />
                <YAxis type="category" dataKey="anio" stroke="#aaa" fontSize={11} width={45} />
                <Tooltip contentStyle={{ background: '#1e1e2e', border: 'none', color: '#fff', fontSize: 12 }}
                  formatter={(val) => [`${val} órdenes`, 'Total']} />
                <Bar dataKey="ordenes" fill="#4cc9f0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={sv.card}>
            <h3 style={sv.cardTitulo}>Detalle por año</h3>
            <div style={sv.tablaHeader}>
              <span style={{ width: 60 }}>AÑO</span>
              <span style={{ flex: 1 }}>DISTRIBUCIÓN</span>
              <span style={{ width: 70, textAlign: 'right' }}>ÓRDENES</span>
            </div>
            {datos.por_anio.map((a, i) => (
              <div key={i} style={{ ...sv.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e' }}>
                <span style={{ width: 60, color: '#4cc9f0', fontWeight: 'bold', fontSize: 14 }}>{a.anio}</span>
                <span style={{ flex: 1 }}>
                  <div style={sv.barrita}>
                    <div style={{ ...sv.barritaFill, width: `${(a.ordenes / maxAnio) * 100}%`, background: '#4cc9f0' }} />
                  </div>
                </span>
                <span style={{ width: 70, textAlign: 'right', color: '#fff', fontWeight: 'bold', fontSize: 13 }}>{a.ordenes}</span>
              </div>
            ))}
            {datos.por_anio.length === 0 && (
              <p style={{ color: '#555', textAlign: 'center', padding: 32 }}>Sin datos de año aún</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Dashboard principal
// ══════════════════════════════════════════════════════════════
export default function Dashboard() {
  const ahora = new Date();
  const [mes,          setMes]          = useState(ahora.getMonth() + 1);
  const [anio,         setAnio]         = useState(ahora.getFullYear());
  const [datos,        setDatos]        = useState(null);
  const [validaciones, setValidaciones] = useState(null);
  const [cargando,     setCargando]     = useState(true);
  const [tabActivo,    setTabActivo]    = useState('ordenes'); // ordenes | vehiculos

  useEffect(() => { cargarDatos(); }, [mes, anio]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [kpis, vals] = await Promise.all([
        api.get(`/dashboard/kpis?mes=${mes}&anio=${anio}`),
        api.get(`/dashboard/validaciones?mes=${mes}&anio=${anio}`)
      ]);
      setDatos(kpis.error ? null : kpis);
      setValidaciones(vals);
    } catch (e) { setDatos(null); }
    finally { setCargando(false); }
  };

  const mesAnterior = () => {
    if (mes === 3 && anio === 2026) return;
    if (mes === 1) { setMes(12); setAnio(anio - 1); }
    else setMes(mes - 1);
  };

  const mesSiguiente = () => {
    const esHoy = mes === ahora.getMonth() + 1 && anio === ahora.getFullYear();
    if (esHoy) return;
    if (mes === 12) { setMes(1); setAnio(anio + 1); }
    else setMes(mes + 1);
  };

  const esMesActual = mes === ahora.getMonth() + 1 && anio === ahora.getFullYear();
  const esMesMinimo = mes === 3 && anio === 2026;

  return (
    <div>
      {/* Header */}
      <div style={s.headerRow}>
        <h2 style={s.titulo}>📊 Dashboard</h2>
        <div style={s.selectorMes}>
          <button style={{...s.flecha, ...(esMesMinimo ? s.flechaDesactivada : {})}} onClick={mesAnterior}>‹</button>
          <span style={s.mesLabel}>{MESES[mes]} {anio}{esMesActual ? ' • Actual' : ''}</span>
          <button style={{...s.flecha, ...(esMesActual ? s.flechaDesactivada : {})}} onClick={mesSiguiente}>›</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #2d2d44', marginBottom: 24 }}>
        {[
          { key: 'ordenes',   label: '📋 Órdenes'   },
          { key: 'vehiculos', label: '🚗 Vehículos'  },
        ].map(t => (
          <button key={t.key}
            style={{
              background: 'none', border: 'none',
              borderBottom: tabActivo === t.key ? '3px solid #4cc9f0' : '3px solid transparent',
              color: tabActivo === t.key ? '#fff' : '#888',
              padding: '10px 24px', cursor: 'pointer',
              fontSize: 15, fontWeight: tabActivo === t.key ? 'bold' : '500',
              marginBottom: '-2px',
            }}
            onClick={() => setTabActivo(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Vehículos ── */}
      {tabActivo === 'vehiculos' && (
        <TabVehiculos mes={mes} anio={anio} esMesActual={esMesActual} />
      )}

      {/* ── Tab Órdenes ── */}
      {tabActivo === 'ordenes' && (
        <>
          {cargando ? (
            <p style={s.cargando}>Cargando...</p>
          ) : !datos ? (
            <p style={s.error}>No se pudo cargar el dashboard</p>
          ) : (
            <>
              <div style={s.kpisRow}>
                <div style={{...s.kpiCard, borderTopColor: '#4cc9f0'}}>
                  <div style={s.kpiValor}>{datos.total_mes}</div>
                  <div style={s.kpiLabel}>Órdenes del mes</div>
                </div>
                <div style={{...s.kpiCard, borderTopColor: '#f72585'}}>
                  <div style={{...s.kpiValor, color: esMesActual ? '#f72585' : '#aaa'}}>
                    {esMesActual ? (datos.ordenes_hoy ?? '—') : '—'}
                  </div>
                  <div style={s.kpiLabel}>Órdenes de hoy</div>
                  {esMesActual && datos.ordenes_hoy > 0 && (
                    <div style={s.kpiBadge}>{((datos.ordenes_hoy / datos.total_mes) * 100).toFixed(0)}% del mes</div>
                  )}
                </div>
                <div style={{...s.kpiCard, borderTopColor: '#7209b7'}}>
                  <div style={{...s.kpiValor, fontSize: 18}}>{datos.tecnico_mes.nombre}</div>
                  <div style={s.kpiLabel}>Técnico del mes 🏆</div>
                  {datos.tecnico_mes.ordenes > 0 && <div style={s.kpiBadge}>{datos.tecnico_mes.ordenes} órdenes</div>}
                </div>
                {validaciones && (
                  <>
                    <div style={{...s.kpiCard, borderTopColor: '#4caf50'}}>
                      <div style={s.kpiValor}>{validaciones.validadas}</div>
                      <div style={s.kpiLabel}>✅ Validadas</div>
                    </div>
                    <div style={{...s.kpiCard, borderTopColor: '#f44336'}}>
                      <div style={s.kpiValor}>{validaciones.no_validadas}</div>
                      <div style={s.kpiLabel}>❌ No validadas</div>
                    </div>
                    <div style={{...s.kpiCard, borderTopColor: '#ffc107'}}>
                      <div style={s.kpiValor}>{validaciones.pendientes}</div>
                      <div style={s.kpiLabel}>⏳ Pendientes</div>
                    </div>
                  </>
                )}
              </div>

              <div style={s.graficasRow}>
                {datos.por_tecnico.length > 0 && (
                  <div style={s.graficaCard}>
                    <h3 style={s.graficaTitulo}>📋 Órdenes por Técnico</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={datos.por_tecnico}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                        <XAxis dataKey="tecnico" stroke="#aaa" fontSize={12} />
                        <YAxis stroke="#aaa" fontSize={12} />
                        <Tooltip contentStyle={{background:'#1e1e2e', border:'none', color:'#fff'}} />
                        <Bar dataKey="total" radius={[4,4,0,0]}>
                          {datos.por_tecnico.map((_, i) => (
                            <Cell key={i} fill={COLORES_TEC[i % COLORES_TEC.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    {datos.por_tecnico.map((t, i) => (
                      <div key={i} style={s.detalleRow}>
                        <div style={{...s.detalleColor, background: COLORES_TEC[i % COLORES_TEC.length]}} />
                        <span style={s.detalleNombre}>{t.tecnico}</span>
                        <div style={s.detalleBarra}>
                          <div style={{ ...s.detalleBarraFill, width: `${(t.total / datos.por_tecnico[0].total) * 100}%`, background: COLORES_TEC[i % COLORES_TEC.length] }} />
                        </div>
                        <span style={{...s.detalleTotal, color: COLORES_TEC[i % COLORES_TEC.length]}}>{t.total}</span>
                      </div>
                    ))}
                  </div>
                )}

                {datos.por_tipo.length > 0 && (
                  <div style={s.graficaCard}>
                    <h3 style={s.graficaTitulo}>🚗 Tipo de Vehículo</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={datos.por_tipo} dataKey="total" nameKey="tipo"
                          cx="50%" cy="50%" outerRadius={90}
                          label={({tipo, total}) => `${tipo}: ${total}`}>
                          {datos.por_tipo.map((_, i) => (
                            <Cell key={i} fill={COLORES_TIPO[i % COLORES_TIPO.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{background:'#1e1e2e', border:'none', color:'#fff'}} />
                      </PieChart>
                    </ResponsiveContainer>
                    {datos.por_tipo.map((t, i) => (
                      <div key={i} style={s.detalleRow}>
                        <div style={{...s.detalleColor, background: COLORES_TIPO[i % COLORES_TIPO.length]}} />
                        <span style={s.detalleNombre}>{t.tipo}</span>
                        <div style={s.detalleBarra}>
                          <div style={{ ...s.detalleBarraFill, width: `${(t.total / datos.por_tipo[0].total) * 100}%`, background: COLORES_TIPO[i % COLORES_TIPO.length] }} />
                        </div>
                        <span style={{...s.detalleTotal, color: COLORES_TIPO[i % COLORES_TIPO.length]}}>{t.total}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {datos.total_mes === 0 && (
                <div style={s.sinDatos}>📭 Sin órdenes en {MESES[mes]} {anio}</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  headerRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  titulo:          { color: '#fff', margin: 0 },
  selectorMes:     { display: 'flex', alignItems: 'center', gap: 12, background: '#1e1e2e', borderRadius: 10, padding: '8px 16px' },
  flecha:          { background: 'none', border: 'none', color: '#4cc9f0', fontSize: 24, cursor: 'pointer', fontWeight: 'bold' },
  flechaDesactivada:{ color: '#444', cursor: 'default' },
  mesLabel:        { color: '#fff', fontSize: 14, fontWeight: 'bold', minWidth: 140, textAlign: 'center' },
  kpisRow:         { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:         { background: '#1e1e2e', borderRadius: 12, padding: 20, borderTop: '3px solid', minWidth: 140, flex: 1 },
  kpiValor:        { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 6 },
  kpiLabel:        { color: '#aaa', fontSize: 12 },
  kpiBadge:        { color: '#4cc9f0', fontSize: 11, marginTop: 4 },
  graficasRow:     { display: 'flex', gap: 16, flexWrap: 'wrap' },
  graficaCard:     { background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 300 },
  graficaTitulo:   { color: '#fff', marginBottom: 16, fontSize: 15 },
  detalleRow:      { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 },
  detalleColor:    { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  detalleNombre:   { color: '#fff', fontSize: 13, width: 80 },
  detalleBarra:    { flex: 1, background: '#2a2a3e', borderRadius: 4, height: 8 },
  detalleBarraFill:{ height: 8, borderRadius: 4 },
  detalleTotal:    { fontSize: 13, fontWeight: 'bold', width: 24 },
  cargando:        { color: '#aaa' },
  error:           { color: '#e63946' },
  sinDatos:        { color: '#555', textAlign: 'center', marginTop: 60, fontSize: 16 },
};

const sv = {
  card:        { background: '#1e1e2e', borderRadius: 12, padding: 20 },
  cardTitulo:  { color: '#fff', fontSize: 14, fontWeight: 'bold', marginBottom: 16 },
  tablaHeader: { display: 'flex', color: '#555', fontSize: 11, fontWeight: 'bold', padding: '8px 0', borderBottom: '1px solid #2d2d44', gap: 8 },
  tablaRow:    { display: 'flex', padding: '10px 0', borderBottom: '1px solid #1a1a2e', gap: 8, alignItems: 'center' },
  barrita:     { background: '#2a2a3e', borderRadius: 3, height: 4, marginTop: 4, overflow: 'hidden' },
  barritaFill: { height: 4, borderRadius: 3 },
};